import React, { MouseEventHandler } from 'react';

import { ButtonVariant } from '@cfa/react-components';
import { Colors } from '@cfa/react-components/dist/cjs/internal/types';

import { cn } from '@/utils/tailwind';

import { PathwayModal } from '@/components/popups/PathwayModal';

interface ConfirmationModalProps {
  bodyText: string;
  bodyTextColor?: string;
  children?: React.ReactNode;
  className?: string;
  headerText: string;
  isDisabled?: boolean;
  isError?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  onClose?: () => void;
  overlayProps?: Record<string, unknown>;
  primaryButtonColor?: Colors;
  primaryButtonHandler?: MouseEventHandler<HTMLButtonElement>;
  primaryButtonText: string;
  primaryButtonVariant?: ButtonVariant;
  secondaryButtonHandler?: MouseEventHandler<HTMLButtonElement>;
  secondaryButtonColor?: Colors;
  secondaryButtonText?: string;
  showCloseButton?: boolean;
  stepKey?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  bodyText,
  bodyTextColor,
  children,
  className,
  headerText,
  isDisabled,
  isError,
  isLoading,
  isOpen,
  onClose,
  overlayProps,
  primaryButtonColor = 'secondary',
  primaryButtonHandler,
  primaryButtonText,
  primaryButtonVariant = 'filled',
  secondaryButtonColor = 'secondary',
  secondaryButtonHandler,
  secondaryButtonText,
  showCloseButton = true,
  stepKey,
}) => (
  <PathwayModal
    className={cn(className, '2xl:max-w-[610px]!')}
    isOpen={isOpen}
    onClose={onClose ?? (() => {})}
    overlayClassName={overlayProps?.className as string | undefined}
    overlayStyle={overlayProps?.style as React.CSSProperties | undefined}
    showCloseButton={showCloseButton}
    stepKey={stepKey}
  >
    <PathwayModal.Header>
      <PathwayModal.Title>{headerText}</PathwayModal.Title>
    </PathwayModal.Header>
    <PathwayModal.Body>
      <div
        className={cn(
          bodyTextColor ? 'text-feedback-success-base!' : 'text-inherit!',
        )}
        dangerouslySetInnerHTML={{ __html: bodyText }}
      />
      {children && <div className="mx-auto">{children}</div>}
    </PathwayModal.Body>
    <PathwayModal.Footer>
      {secondaryButtonHandler && secondaryButtonText && (
        <PathwayModal.SecondaryButton
          color={secondaryButtonColor}
          onClick={secondaryButtonHandler}
          testId="ConfirmationPopUpSecondaryButton"
        >
          {secondaryButtonText}
        </PathwayModal.SecondaryButton>
      )}
      <PathwayModal.PrimaryButton
        color={primaryButtonColor}
        disabled={isDisabled}
        loading={isLoading}
        onClick={primaryButtonHandler}
        testId="ConfirmationPopUpPrimaryButton"
        variant={isError ? 'destructive' : primaryButtonVariant}
      >
        {primaryButtonText}
      </PathwayModal.PrimaryButton>
    </PathwayModal.Footer>
  </PathwayModal>
);

export default ConfirmationModal;
